import React, { FC, memo, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { TableFilter, useTableFilterContext } from 'features/TableFilter';
import { WarehouseSelect } from './WarehouseSelect';
import { WaitingListFilterKeys } from '../../model/types';
import { UnitTypeSelect } from './UnitTypeSelect';
import { RangeDatePicker } from 'shared/ui/RangeDatePicker/RangeDatePicker';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { RangeInput } from 'shared/ui/RangeInput/RangeInput';

export const WaitingListFilter: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);

  const { filters, changeFilters, clearCurrentFilters } = useTableFilterContext<WaitingListFilterKeys>();

  const selectWarehouse = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: WaitingListFilterKeys.selectedWarehouseId, label, value, title: t('Warehouse') });
    },
    [changeFilters, t],
  );

  const changePreferredDateFrom = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: WaitingListFilterKeys.preferredDateFrom,
        label: t('From {{deadlineDateFrom}}', { deadlineDateFrom: clientFormatDateFrom }),
        value,
        title: t('Preferred date'),
      });
    },
    [changeFilters, t],
  );

  const changePreferredDateTo = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: WaitingListFilterKeys.preferredDateTo,
        label: t('To {{deadlineDateTo}}', { deadlineDateTo: clientFormatDateTo }),
        value,
        title: t('Preferred date'),
      });
    },
    [changeFilters, t],
  );

  const changeUnitType = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: WaitingListFilterKeys.unitType, label, value, title: t('Unit type') });
    },
    [changeFilters, t],
  );

  const changeSquareFrom = useCallback(
    (value: Nullable<number>) => {
      changeFilters({
        key: WaitingListFilterKeys.squareFrom,
        label: t('From {{squareFrom}} m²', { squareFrom: value }),
        value,
        title: t('Square'),
      });
    },
    [changeFilters, t],
  );

  const changeSquareTo = useCallback(
    (value: Nullable<number>) => {
      changeFilters({
        key: WaitingListFilterKeys.squareTo,
        label: t('To {{squareTo}} m²', { squareTo: value }),
        value,
        title: t('Square'),
      });
    },
    [changeFilters, t],
  );

  return (
    <TableFilter>
      <div className="space-y-2">
        <WarehouseSelect value={filters.selectedWarehouseId?.value} onChange={selectWarehouse} onClear={clearCurrentFilters} />
        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Preferred date range')}
          onChangeFrom={changePreferredDateFrom}
          onChangeTo={changePreferredDateTo}
          startOfDay
          format={CLIENT_DATE_FORMAT}
          values={[
            filters.preferredDateFrom?.value && dayjs(filters.preferredDateFrom?.value),
            filters.preferredDateTo?.value && dayjs(filters.preferredDateTo?.value),
          ]}
        />

        <UnitTypeSelect value={filters.unitType?.value} onChange={changeUnitType} onClear={clearCurrentFilters} />

        <RangeInput
          values={[filters.squareFrom?.value, filters.squareTo?.value]}
          placeholder={[t('From'), t('To')]}
          label={t('Square')}
          type="number"
          onChangeFrom={changeSquareFrom}
          onChangeTo={changeSquareTo}
        />
      </div>
    </TableFilter>
  );
});
